// packages/agent-kit/src/analytics/sessionService.ts

import { v4 as uuidv4 } from 'uuid';
import type { AxiosInstance } from 'axios';
import { getApiClient } from '@dopomogai/api-client';
import type { EnsureVisitResponse } from '@dopomogai/types';

const VISITOR_ID_KEY = 'dopomogai_visitor_id';
const SESSION_ID_KEY = 'dopomogai_session_id';
const LEAD_RECORD_ID_KEY = 'dopomogai_lead_record_id';

class SessionService {
  private visitorId: string | null = null;
  private sessionId: string | null = null;
  private leadRecordId: string | null = null;
  private _isInitialized = false;
  private isInitializing = false;

  async initialize(): Promise<void> {
    if (this._isInitialized || this.isInitializing) return;

    if (typeof window === 'undefined') {
      this.visitorId = 'server';
      this.sessionId = 'server';
      this._isInitialized = true;
      return;
    }

    try {
      this.isInitializing = true;
      this.visitorId = this.getOrCreateVisitorId();
      this.sessionId = this.getOrCreateSessionId();
      await this.ensureLeadRecord();
      this._isInitialized = true;
    } catch (error) {
      console.warn('SessionService initialization failed:', error);
      this._isInitialized = true;
    } finally {
      this.isInitializing = false;
    }
  }

  private getOrCreateVisitorId(): string {
    if (this.visitorId) return this.visitorId;

    let visitorId = null;
    try {
      visitorId = localStorage.getItem(VISITOR_ID_KEY);
    } catch (error) {
      console.warn('Failed to read visitor ID from localStorage:', error);
    }

    if (!visitorId) {
      visitorId = uuidv4();
      try {
        localStorage.setItem(VISITOR_ID_KEY, visitorId);
      } catch (error) {
        console.warn('Failed to store visitor ID in localStorage:', error);
      }
    }

    this.visitorId = visitorId;
    return visitorId;
  }

  private getOrCreateSessionId(): string {
    if (this.sessionId) return this.sessionId;

    let sessionId = null;
    try {
      sessionId = sessionStorage.getItem(SESSION_ID_KEY);
    } catch (error) {
      console.warn('Failed to read session ID from sessionStorage:', error);
    }

    if (!sessionId) {
      sessionId = uuidv4();
      try {
        sessionStorage.setItem(SESSION_ID_KEY, sessionId);
      } catch (error) {
        console.warn('Failed to store session ID in sessionStorage:', error);
      }
    }

    this.sessionId = sessionId;
    return sessionId;
  }

  private async ensureLeadRecord(): Promise<void> {
    if (this.leadRecordId) return;
    if (!this.visitorId) {
      console.warn('Cannot ensure lead record without a visitor ID.');
      return;
    }

    try {
      const stored = sessionStorage.getItem(LEAD_RECORD_ID_KEY);
      if (stored) {
        this.leadRecordId = stored;
        return;
      }
    } catch (error) {
      console.warn('Failed to read lead record ID from sessionStorage:', error);
    }

    let client: AxiosInstance | null = null;
    try {
      client = getApiClient() as unknown as AxiosInstance;
    } catch (err) {
      console.warn('API client not configured yet; skipping lead record ensure.', err);
      return;
    }
    if (!client || typeof client.post !== 'function') {
      console.warn('API client not ready; skipping lead record ensure.');
      return;
    }

    const payload = {
      visitor_id: this.visitorId,
      source: typeof document !== 'undefined' && document.referrer ? document.referrer : 'direct',
      path:
        typeof window !== 'undefined' && window.location
          ? window.location.pathname + window.location.search
          : undefined,
    };

    try {
      const response = await client.post<EnsureVisitResponse>('/leads/ensure-visit', payload);
      const leadId = response?.data?.lead_id;
      if (leadId) {
        this.leadRecordId = leadId;
        try {
          sessionStorage.setItem(LEAD_RECORD_ID_KEY, leadId);
        } catch (error) {
          console.warn('Failed to store lead record ID in sessionStorage:', error);
        }
      }
    } catch (error) {
      console.warn('Failed to ensure lead record:', error);
    }
  }

  getVisitorId(): string {
    if (!this.visitorId) {
      this.visitorId = this.getOrCreateVisitorId();
    }
    return this.visitorId;
  }

  getSessionId(): string {
    if (!this.sessionId) {
      this.sessionId = this.getOrCreateSessionId();
    }
    return this.sessionId;
  }

  getLeadRecordId(): string | null {
    if (!this.leadRecordId) {
      try {
        this.leadRecordId = sessionStorage.getItem(LEAD_RECORD_ID_KEY);
      } catch (error) {
        console.warn('Failed to get lead record ID from sessionStorage:', error);
      }
    }
    return this.leadRecordId;
  }

  isInitialized(): boolean {
    return this._isInitialized;
  }
}

export const sessionService = new SessionService();
